import React, { useState } from "react";
import { Search, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

const sha256 = async (text) => {
  const buf = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
};

const extractEmails = (raw) => [...new Set((raw.match(EMAIL_RE) || []).map((e) => e.trim().toLowerCase()))];

const MatchSummary = ({ a }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="kohorten-match-summary">
    {[["eingegeben", "eingegeben"], ["gefunden", "gefunden"], ["nicht_gefunden", "nicht gefunden"], ["davon_testkonten", "davon Testkonten"], ["davon_bezahlt", "davon bezahlt"], ["davon_status_nicht_zuordenbar", "Status nicht zuordenbar"], ["ausgewertet", "ausgewertet"]].map(([k, l]) => (
      <div key={k} className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
        <p className="text-[11px] uppercase tracking-wider text-slate-500">{l}</p>
        <p className="text-xl font-extrabold text-slate-900 tabular-nums" data-testid={`kohorten-match-${k}`}>{a[k]}</p>
      </div>
    ))}
  </div>
);

// Einmaliger Abgleich: Adressen werden im Browser gehasht, nichts wird gespeichert.
export const CohortMatch = ({ renderTables }) => {
  const [raw, setRaw] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const count = extractEmails(raw).length;

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const hashes = await Promise.all(extractEmails(raw).map(sha256));
      const r = await api.post("/admin/user-cohort-report/match", { email_hashes: hashes });
      setResult(r.data);
      setRaw("");
    } catch (_e) {
      setError("Abgleich fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl bg-white border border-slate-200 p-4 space-y-3" data-testid="kohorten-match-section">
      <div>
        <h2 className="text-sm font-bold text-slate-900">Abgleich mit Adressliste</h2>
        <p className="text-xs text-slate-500">Adressen oder ganze Tabelle einfügen. Sie werden im Browser gehasht (SHA-256), der Server speichert nichts. Ausgabe nur als Zahlen.</p>
      </div>
      <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={4} data-testid="kohorten-match-input" className="w-full rounded-lg border border-slate-300 p-2 text-sm font-mono" placeholder="Adressen hier einfügen …" />
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={run} disabled={busy || count === 0} data-testid="kohorten-match-run-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#4563BB] hover:opacity-90 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">
          <Search className="w-4 h-4" /> {count} Adressen abgleichen
        </button>
        {result && (
          <button type="button" onClick={() => setResult(null)} data-testid="kohorten-match-clear-btn" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-2 hover:bg-slate-50">
            <Trash2 className="w-4 h-4" /> Ergebnis verwerfen
          </button>
        )}
      </div>
      {error && <p className="text-sm text-red-600" data-testid="kohorten-match-error">{error}</p>}
      {result && (
        <div className="space-y-4" data-testid="kohorten-match-result">
          <MatchSummary a={result.abgleich} />
          {renderTables(result.status)}
        </div>
      )}
    </section>
  );
};

export default CohortMatch;
